import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FilterDefinition, FilterValues, ProviderEvent} from '../../models/vte';
import {AuthService} from '../../services/auth.service';
import {ReportingDateTimeService} from '../../shared/datetime/reporting-datetime.service';
import {OperationsFiltersService} from '../../services/vte/operations-filters.service';
import {ProviderEventViewService} from '../../services/vte/provider-event-view.service';
import {VteApiService} from '../../services/vte/vte-api.service';
import {
    applyPaginatedResponse,
    createVteListState,
    sortDirectionForField,
    toggleOrdering,
    VteListState,
} from '../../services/vte/vte-list.utils';
import {FilterBarComponent, ProviderEventStatusBadgeComponent, VtePaginationComponent} from '../../shared/vte';

@Component({
    selector: 'app-provider-event-list',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        FilterBarComponent,
        ProviderEventStatusBadgeComponent,
        VtePaginationComponent,
    ],
    templateUrl: './provider-event-list.component.html',
    styleUrl: './provider-event-list.component.css',
})
export class ProviderEventListComponent implements OnInit {
    readonly listState: VteListState = createVteListState();
    filterDefinitions: FilterDefinition[] = [];
    filterValues: FilterValues = {};
    events: ProviderEvent[] = [];
    private organizationNames = new Map<number, string>();

    readonly sortableColumns = [
        {key: 'received_at', label: 'Received'},
        {key: 'processing_status', label: 'Status'},
        {key: 'provider_id', label: 'Provider'},
        {key: 'provider_timestamp', label: 'Provider Time'},
    ];

    constructor(
        private vteApi: VteApiService,
        private filtersService: OperationsFiltersService,
        private providerEventView: ProviderEventViewService,
        private auth: AuthService,
        private route: ActivatedRoute,
        private reportingDateTime: ReportingDateTimeService,
    ) {
    }

    get showOrganizationColumn(): boolean {
        return this.auth.isSuperUser();
    }

    eventDetailQueryParams(event: ProviderEvent): Record<string, string> {
        const next: Record<string, string> = {};
        this.route.snapshot.queryParamMap.keys.forEach((key) => {
            const value = this.route.snapshot.queryParamMap.get(key);
            if (value) {
                next[key] = value;
            }
        });
        if (event.provider_id) {
            next['provider_id'] = event.provider_id;
        }
        return next;
    }

    ngOnInit(): void {
        this.filtersService.loadAllFilterSupportData().subscribe({
            next: ({cameras, organizations}) => {
                this.organizationNames = new Map(
                    (organizations || []).map((organization) => [organization.id, organization.name]),
                );
                this.filterDefinitions = this.filtersService.getProviderEventFilterDefinitions(
                    cameras,
                    organizations,
                );
            },
        });
        this.loadEvents();
    }

    organizationLabel(organizationId: number | null | undefined): string {
        if (organizationId == null) {
            return '—';
        }
        return this.organizationNames.get(organizationId) || String(organizationId);
    }

    loadEvents(): void {
        this.listState.loading = true;
        const params = this.providerEventView.buildListParams(
            this.filterValues,
            this.listState.currentPage,
            this.listState.pageSize,
            this.listState.ordering,
        );

        this.vteApi.listProviderEvents(params).subscribe({
            next: (response) => {
                this.events = response.results || [];
                applyPaginatedResponse(this.listState, response);
                this.listState.loading = false;
            },
            error: () => {
                this.events = [];
                this.listState.totalRecords = 0;
                this.listState.loading = false;
            },
        });
    }

    onFiltersApply(values: FilterValues): void {
        this.filterValues = values;
        this.listState.currentPage = 1;
        this.loadEvents();
    }

    onFiltersReset(): void {
        this.filterValues = {};
        this.listState.currentPage = 1;
        this.loadEvents();
    }

    onPageChange(page: number): void {
        this.listState.currentPage = page;
        this.loadEvents();
    }

    onPageSizeChange(pageSize: number): void {
        this.listState.pageSize = pageSize;
        this.listState.currentPage = 1;
        this.loadEvents();
    }

    onSort(field: string): void {
        this.listState.ordering = toggleOrdering(field, this.listState.ordering);
        this.loadEvents();
    }

    sortIndicator(field: string): string {
        const direction = sortDirectionForField(field, this.listState.ordering);
        if (direction === 'asc') {
            return '↑';
        }
        if (direction === 'desc') {
            return '↓';
        }
        return '';
    }

    providerLabel(providerId: string): string {
        return this.providerEventView.getProviderLabel(providerId);
    }

    normalizedPreview(event: ProviderEvent): string {
        const entries = this.providerEventView.buildNormalizedFieldEntries(event);
        if (!entries.length) {
            return '—';
        }
        const preview = entries.slice(0, 2).map((entry) => `${entry.label}: ${entry.value}`);
        return preview.join(' · ');
    }

    formatTimestamp(value: string | null | undefined): string {
        if (!value) {
            return '—';
        }
        const formatted = this.reportingDateTime.format(value);
        return formatted || value;
    }

    serialNumber(rowIndex: number): number {
        return (this.listState.currentPage - 1) * this.listState.pageSize + rowIndex + 1;
    }
}
